/* ==========================================================================
   OFFMARKET — interaction layer
   --------------------------------------------------------------------------
   Boot sequence, grain, scroll rail, press response, magnetic controls,
   scramble, horizontal scrollers, and the rotating spotlight.

   Rules this file obeys, because an interaction layer that breaks a storefront
   is worse than no interaction layer:

     1. Nothing here is required to render content. Every element it touches is
        either injected by this script or only has properties added to it.
     2. Every module is wrapped. One module throwing cannot stop the others.
     3. The boot curtain registers its own removal timer BEFORE it is shown, so
        a later failure can never leave a visitor staring at a black screen.
     4. prefers-reduced-motion turns off the theatre — but not the usability
        work. Wheel support on a sideways strip and picking a spotlight product
        are not motion, so they run for everyone.

   Ritual scrolls .page-wrapper rather than the document, so scroll position is
   read from that element when it exists.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  const fine = window.matchMedia('(pointer: fine)').matches;
  const scroller = () => document.querySelector('.page-wrapper') || document.scrollingElement;

  /* run a module in isolation — a throw is logged, never propagated */
  const mod = (name, fn) => {
    try {
      fn();
    } catch (e) {
      console.warn(`[offmarket-fx] ${name} skipped:`, e && e.message);
    }
  };

  const el = (tag, cls, html) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };

  /* ========================================================================
     Horizontal scrollers — usability, so it runs regardless of motion setting
     A strip that scrolls sideways should answer a normal mouse wheel. Without
     this the collection strip can only be dragged, which is close to invisible
     as an affordance on a desktop pointer.
     ====================================================================== */
  mod('hscroll', () => {
    /* The element that scrolls is rarely the one with the carousel class. In
       Ritual the collection strip is <slideshow-component class="resource-list
       __carousel"> but the overflow lives two levels down on <slideshow-slides>,
       which carries no class at all — matching on the wrapper found an element
       whose scrollWidth equalled its clientWidth, so an earlier version of this
       silently did nothing. Resolve the real scroller by measurement. */
    const findScroller = (host) => {
      if (host.scrollWidth > host.clientWidth + 4) return host;
      return (
        [...host.querySelectorAll('*')].find((n) => n.scrollWidth > n.clientWidth + 4) || null
      );
    };

    document
      .querySelectorAll(
        'slideshow-slides, .resource-list__carousel, [class*="carousel"], .om-hscroll'
      )
      .forEach((host) => {
        if (host.dataset.omHscroll === '1') return;
        const strip = findScroller(host);
        if (!strip) return;
        host.dataset.omHscroll = '1';
        strip.dataset.omHscroll = '1';

        /* listen on the host so the wheel works anywhere over the strip, but
           move the element that actually scrolls */
        host.addEventListener(
          'wheel',
          (e) => {
            // let a genuinely sideways gesture (trackpad) behave natively
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            const max = strip.scrollWidth - strip.clientWidth;
            const atStart = strip.scrollLeft <= 0;
            const atEnd = strip.scrollLeft >= max - 1;
            // at either end, hand the gesture back to the page
            if ((atStart && e.deltaY < 0) || (atEnd && e.deltaY > 0)) return;
            e.preventDefault();
            strip.scrollLeft = Math.max(0, Math.min(max, strip.scrollLeft + e.deltaY));
          },
          { passive: false }
        );
      });
  });

  /* ========================================================================
     Spotlight
     Which product is shown is picked at random on every load, so the section
     is never the same twice and no single product owns the slot. Rotation is
     a crossfade on a long interval — long enough to read, not a carousel.

     Picking runs for everyone. Only the automatic advance is motion-gated: an
     element that changes on its own is exactly what reduced-motion is for.
     ====================================================================== */
  mod('spotlight', () => {
    document.querySelectorAll('[data-om-spotlight]').forEach((stage) => {
      const slides = [...stage.querySelectorAll('.om-spot__slide')];
      if (slides.length < 1) return;

      const ticks = [...stage.querySelectorAll('.om-spot__tick')];
      const interval = Math.max(8000, Number(stage.dataset.interval) || 27000);

      // Fisher-Yates, so every product has an equal chance of leading
      const order = slides.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }

      let pos = 0;

      const show = (idx) => {
        slides.forEach((s, i) => {
          const on = i === idx;
          s.classList.toggle('om-spot__slide--on', on);
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
          // a hidden slide must not be reachable by keyboard
          s.tabIndex = on ? 0 : -1;
        });
        ticks.forEach((t, i) => t.classList.toggle('om-spot__tick--on', order[i] === idx));
      };

      show(order[0]);

      if (REDUCED || slides.length < 2) return;

      let timer = null;
      const advance = () => {
        pos = (pos + 1) % order.length;
        show(order[pos]);
      };
      const start = () => {
        if (timer === null) timer = setInterval(advance, interval);
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };

      // do not rotate under the pointer, or in a background tab
      stage.addEventListener('pointerenter', stop);
      stage.addEventListener('pointerleave', start);
      stage.addEventListener('focusin', stop);
      stage.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () =>
        document.hidden ? stop() : start()
      );

      ticks.forEach((t, i) => {
        t.addEventListener('click', () => {
          pos = i;
          show(order[pos]);
          stop();
          start();
        });
      });

      start();
    });
  });

  /* Everything below is theatre. A visitor who has asked their OS for less
     motion gets the typography, the layout and the spotlight — and none of it. */
  if (REDUCED) return;

  /* ========================================================================
     Grain
     ====================================================================== */
  mod('grain', () => {
    const g = el('div', 'om-grain');
    g.setAttribute('aria-hidden', 'true');
    document.body.appendChild(g);
  });

  /* ========================================================================
     Scroll rail
     ====================================================================== */
  mod('rail', () => {
    const rail = el('div', 'om-rail', '<div class="om-rail__fill"></div>');
    rail.setAttribute('aria-hidden', 'true');
    document.body.appendChild(rail);
    const fill = rail.firstElementChild;

    let ticking = false;
    const draw = () => {
      const s = scroller();
      if (!s) return;
      const max = s.scrollHeight - s.clientHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, s.scrollTop / max)) : 0;
      fill.style.setProperty('--om-p', String(p));
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(draw);
    };

    (scroller() || window).addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    draw();
  });

  /* The custom cursor was removed after review: replacing the system pointer
     is a cost every visitor pays on every page for a flourish, and on a store
     the pointer needs to behave exactly as expected. The hover responses it
     was signalling all still fire from the elements themselves.

     The scroll-velocity skew was removed for the same kind of reason: tying a
     transform to velocity re-fires on every wheel tick, so it read as a
     continuous shake rather than one movement. Arrival is the reveal's job in
     offmarket-motion.js, and that plays once per element. */

  /* ========================================================================
     Boot sequence — once per session
     ====================================================================== */
  mod('boot', () => {
    let already = false;
    try {
      already = sessionStorage.getItem('om-booted') === '1';
      sessionStorage.setItem('om-booted', '1');
    } catch (e) {
      already = true; /* private mode: skip rather than replay every page */
    }
    if (already) return;
    if (root.classList.contains('shopify-design-mode')) return;

    const word = (document.querySelector('meta[property="og:site_name"]')?.content || 'Offmarket')
      .trim()
      .slice(0, 18);

    const boot = el(
      'div',
      'om-boot',
      `<div class="om-boot__mark"><span>${word}</span></div>
       <div class="om-boot__bar"></div>
       <div class="om-boot__num">000</div>`
    );
    boot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(boot);

    const kill = () => {
      boot.classList.add('om-boot--out');
      setTimeout(() => boot.remove(), 1100);
    };

    /* registered before anything can fail */
    const failsafe = setTimeout(kill, 3200);

    const bar = boot.querySelector('.om-boot__bar');
    const num = boot.querySelector('.om-boot__num');
    const t0 = performance.now();
    const DUR = 1150;

    const tick = (now) => {
      const p = Math.min(1, (now - t0) / DUR);
      const eased = 1 - Math.pow(1 - p, 3);
      bar.style.setProperty('--om-p', String(eased));
      num.textContent = String(Math.round(eased * 100)).padStart(3, '0');
      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        clearTimeout(failsafe);
        setTimeout(kill, 180);
      }
    };
    requestAnimationFrame(tick);
  });

  /* ========================================================================
     Press response
     ====================================================================== */
  mod('press', () => {
    document.addEventListener(
      'pointerdown',
      (e) => {
        const t = e.target instanceof Element ? e.target.closest('.button, .om-cta') : null;
        if (!t) return;
        t.classList.remove('om-pressed');
        void t.offsetWidth; /* restart the animation on a repeat press */
        t.classList.add('om-pressed');
        setTimeout(() => t.classList.remove('om-pressed'), 560);
      },
      { passive: true }
    );
  });

  /* ========================================================================
     Magnetic controls
     ====================================================================== */
  mod('magnet', () => {
    if (!fine) return;
    const PULL = 0.32;
    const RANGE = 90;

    document.addEventListener(
      'pointermove',
      (e) => {
        const t = e.target instanceof Element ? e.target.closest('.om-magnet') : null;
        if (!t) return;
        const r = t.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        if (Math.abs(dx) > r.width / 2 + RANGE || Math.abs(dy) > r.height / 2 + RANGE) return;
        t.classList.add('om-magnet--active');
        t.style.setProperty('--om-mx', `${dx * PULL}px`);
        t.style.setProperty('--om-my', `${dy * PULL}px`);
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerout',
      (e) => {
        const t = e.target instanceof Element ? e.target.closest('.om-magnet') : null;
        if (!t) return;
        t.classList.remove('om-magnet--active');
        t.style.setProperty('--om-mx', '0px');
        t.style.setProperty('--om-my', '0px');
      },
      { passive: true }
    );
  });

  /* ========================================================================
     Scramble
     Text resolves out of mono noise on arrival. Only short, single-text-node
     elements qualify — churning a paragraph is noise, churning a title is a
     mechanism. The original string is restored exactly.
     ====================================================================== */
  mod('scramble', () => {
    const GLYPHS = '#$%&*+=-_/\\<>[]{}0123456789';
    const MAX = 46;

    const run = (node) => {
      const original = node.textContent;
      if (!original || original.length > MAX) return;
      const chars = [...original];
      node.classList.add('om-scrambling');

      const t0 = performance.now();
      const DUR = 620;

      const tick = (now) => {
        const p = Math.min(1, (now - t0) / DUR);
        const settled = Math.floor(p * chars.length);
        node.textContent = chars
          .map((c, i) => {
            if (i < settled || c === ' ') return c;
            return GLYPHS[(Math.random() * GLYPHS.length) | 0];
          })
          .join('');
        if (p < 1) {
          requestAnimationFrame(tick);
        } else {
          node.textContent = original;
          node.classList.remove('om-scrambling');
        }
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          run(entry.target);
        });
      },
      { threshold: 0.9 }
    );

    const tag = () => {
      document.querySelectorAll('[data-om-scramble]').forEach((n) => {
        if (n.dataset.omScrambleReady === '1') return;
        if (n.children.length) return; /* single text node only */
        n.dataset.omScrambleReady = '1';
        io.observe(n);
      });
    };

    tag();
    document.addEventListener('shopify:section:load', tag);
  });
})();
